// Core packages

// Third party packages
import { FormlyFieldConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";

// Custom packages
import {
  AREA_CODE_PATTERN,
  buildDataTypeLabels,
  DESCRIPTION_MAX_LENGTH,
  DOC_KEY_PATTERN,
  labelHasLetterOrDigit,
  TRANSLATION_MAX_LENGTH,
  TRANSLATION_MIN_LENGTH,
} from "src/app/modules/settings/document-template.utils";
import { DataTypeSummary } from "src/app/shared/interfaces/data-type.interface";

/** A plain select option */
export interface FieldOption {
  value: any;
  label: string;
}

/**
 * Tells whether a label is already used by a sibling node.
 *
 * Returns the message to show when it is, '' when the label is free. The wording is
 * owned by the caller, which is the only one knowing whether it is validating an area
 * or a section.
 *
 * @since 3.0.0
 */
export type LabelConflictChecker = (value: string) => string;

/**
 * Builders of the Formly fields shared by the document-template page (levels 1 and 2)
 * and by its third level dialog. Every label goes through the translate service: no
 * field of this feature carries a hardcoded string.
 *
 * Not an Angular service on purpose: it holds no state and is built where it is used.
 *
 * @since 3.0.0
 */
export class DocumentTemplateFieldFactory {
  /**
   * Class constructor
   */
  constructor(private translateService: TranslateService) {}

  /**
   * Area code. Identifier: it ends up in the documents already uploaded on the
   * properties and in the QR codes already printed.
   *
   * @since 3.0.0
   */
  areaCode(className: string): FormlyFieldConfig {
    return {
      className,
      type: "input",
      key: "code",
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.code"),
        required: true,
        maxLength: 5,
        description: this.instant("settings.documentTemplate.hints.code"),
      },
      validators: {
        areaCode: {
          expression: (control: any): boolean =>
            !control.value || AREA_CODE_PATTERN.test(control.value),
          message: (): string =>
            this.instant("settings.documentTemplate.errors.codePattern"),
        },
      },
    };
  }

  /**
   * Section code. Free form identifier of the section inside its area.
   *
   * @since 3.0.0
   */
  contentCode(className: string): FormlyFieldConfig {
    return {
      className,
      type: "input",
      key: "code",
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.contentCode"),
        required: true,
        maxLength: 20,
        description: this.instant("settings.documentTemplate.hints.contentCode"),
      },
    };
  }

  /**
   * Document key: the domain code used as join key with the uploaded documents.
   *
   * @since 3.0.0
   */
  docKey(className: string): FormlyFieldConfig {
    return {
      className,
      type: "input",
      key: "key",
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.key"),
        required: true,
        maxLength: 20,
        description: this.instant("settings.documentTemplate.hints.key"),
      },
      validators: {
        docKey: {
          expression: (control: any): boolean =>
            !control.value || DOC_KEY_PATTERN.test(control.value),
          message: (): string =>
            this.instant("settings.documentTemplate.errors.keyPattern"),
        },
      },
    };
  }

  /**
   * One of the two translations of a node: the only text the user edits, and since the
   * internal identifier is generated out of it, the only one carrying the back-end
   * uniqueness rules too.
   *
   * @since 3.0.0
   *
   * @param language         Which of the two labels
   * @param className        Bootstrap grid classes
   * @param [checkDuplicate] Sibling lookup, so that a label already in use is reported
   *                         while the user types instead of after a rejected save.
   *                         Omit it where the siblings are not known: the back-end
   *                         answers 409 and its message is shown as is.
   */
  translation(
    language: "it" | "en",
    className: string,
    checkDuplicate?: LabelConflictChecker
  ): FormlyFieldConfig {
    const validators: any = {};

    if (language === "en") {
      // The internal identifier is built out of the english label: a label with nothing
      // but punctuation in it is refused by the back-end with a 400
      validators.labelIsUsable = {
        expression: (control: any): boolean =>
          !control.value || labelHasLetterOrDigit(control.value),
        message: (): string =>
          this.instant("settings.documentTemplate.errors.labelWithoutLetters"),
      };
    }

    if (checkDuplicate) {
      validators.labelIsFree = {
        expression: (control: any): boolean => !checkDuplicate(control.value),
        message: (error: any, field: FormlyFieldConfig): string =>
          checkDuplicate(field && field.formControl ? field.formControl.value : ""),
      };
    }

    const config: FormlyFieldConfig = {
      className,
      type: "input",
      key: `translation.${language}`,
      templateOptions: {
        label: this.instant(
          `settings.documentTemplate.fields.translation.${language}`
        ),
        required: true,
        minLength: TRANSLATION_MIN_LENGTH,
        maxLength: TRANSLATION_MAX_LENGTH,
      },
    };
    if (Object.keys(validators).length) {
      config.validators = validators;
    }
    return config;
  }

  /**
   * Free text description of a document type: optional, plain text, multiline.
   *
   * `maxLength` is also rendered by Formly as the native `maxlength` attribute, so the
   * browser itself truncates a longer paste: the back-end can never refuse the text for
   * its length. The character counter lives in the hint because the form-field wrapper
   * of @ngx-formly/material 5 renders nothing but `description` under the control.
   *
   * @since 3.0.0
   */
  description(className: string): FormlyFieldConfig {
    return {
      className,
      type: "textarea",
      key: "description",
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.description"),
        required: false,
        maxLength: DESCRIPTION_MAX_LENGTH,
        rows: 4,
        description: this.descriptionHint(""),
      },
      expressionProperties: {
        // Read from the control, which is updated on every keystroke; the model is
        // only the fallback while the control does not exist yet
        "templateOptions.description": (
          model: any,
          formState: any,
          field?: FormlyFieldConfig
        ): string =>
          this.descriptionHint(
            field && field.formControl
              ? field.formControl.value
              : model
              ? model.description
              : ""
          ),
      },
    };
  }

  /**
   * Hint of the description field, character counter included. The count is the one
   * the native `maxlength` enforces: the raw text, surrounding spaces included.
   *
   * @since 3.0.0
   */
  private descriptionHint(value: any): string {
    return this.instant("settings.documentTemplate.hints.description", {
      count: typeof value === "string" ? value.length : 0,
      max: DESCRIPTION_MAX_LENGTH,
    });
  }

  /**
   * Area group select.
   *
   * @since 3.0.0
   */
  areaGroup(className: string): FormlyFieldConfig {
    return this.select(
      "group",
      "settings.documentTemplate.fields.group",
      className,
      [
        this.option(
          "amministrativo",
          "settings.documentTemplate.groups.amministrativo"
        ),
        this.option("contratti", "settings.documentTemplate.groups.contratti"),
        this.option("tecnico", "settings.documentTemplate.groups.tecnico"),
        this.option(
          "unitaImmobiliari",
          "settings.documentTemplate.groups.unitaImmobiliari"
        ),
      ]
    );
  }

  /**
   * Area render type select.
   *
   * @since 3.0.0
   */
  areaType(className: string): FormlyFieldConfig {
    return this.select(
      "type",
      "settings.documentTemplate.fields.type",
      className,
      [
        this.option("accordion", "settings.documentTemplate.types.accordion"),
        this.option("link", "settings.documentTemplate.types.link"),
      ]
    );
  }

  /**
   * Section element type select: a plain section, or one repeated per element of a
   * condo structure.
   *
   * @since 3.0.0
   */
  elementType(className: string): FormlyFieldConfig {
    return this.select(
      "elementType",
      "settings.documentTemplate.fields.elementType",
      className,
      [
        this.option("object", "settings.documentTemplate.elementTypes.object"),
        this.option("array", "settings.documentTemplate.elementTypes.array"),
      ]
    );
  }

  /**
   * Condo structure select, shown only while the section is of type "array".
   *
   * @since 3.0.0
   *
   * @param className     Bootstrap grid classes
   * @param options       Structures to offer (rebuilt by the caller, never appended to)
   * @param modelAccessor Returns the model the hideExpression must read
   */
  structure(
    className: string,
    options: FieldOption[],
    modelAccessor: () => any
  ): FormlyFieldConfig {
    return {
      className,
      type: "select",
      key: "structure",
      hideExpression: (): boolean => {
        const model = modelAccessor();
        return !model || model.elementType !== "array";
      },
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.structure"),
        required: false,
        multiple: false,
        options,
        description: this.instant("settings.documentTemplate.hints.structure"),
      },
    };
  }

  /**
   * Data type select: the data entry form shown when the document is uploaded on a
   * property. The option VALUE is the id of the data type, its label is the readable
   * name of the schema.
   *
   * Deleted data types are never offered. The one currently stored on the document is
   * kept in the list even when it is deleted or missing from the catalogue, flagged as
   * unavailable, so that opening the editor can neither hide nor silently rewrite it.
   *
   * @since 3.0.0
   *
   * @param className    Bootstrap grid classes
   * @param dataTypes    Catalogue, deleted rows included (they are filtered out here)
   * @param currentValue dataTypeId stored on the document ('' when creating)
   * @param required     True on creation, where the back-end demands the reference
   */
  dataTypeId(
    className: string,
    dataTypes: DataTypeSummary[],
    currentValue: string,
    required: boolean
  ): FormlyFieldConfig {
    const catalog = Array.isArray(dataTypes) ? dataTypes : [];
    const labels = buildDataTypeLabels(catalog);
    const options: FieldOption[] = [];

    catalog.forEach((dataType) => {
      if (!dataType || !dataType._id || dataType.deleted) {
        return;
      }
      const id = dataType._id.toString();
      options.push({ value: id, label: labels[id] });
    });
    options.sort((a, b) => a.label.localeCompare(b.label));

    const current = currentValue ? currentValue.toString() : "";
    if (current && !options.some((option) => option.value === current)) {
      options.unshift({
        value: current,
        label: `${labels[current] ? labels[current] : current} — ${this.instant(
          "settings.documentTemplate.errors.dataTypeUnavailable"
        )}`,
      });
    }

    return {
      className,
      type: "select",
      key: "dataTypeId",
      templateOptions: {
        label: this.instant("settings.documentTemplate.fields.dataType"),
        required,
        multiple: false,
        options,
        description: this.instant("settings.documentTemplate.hints.dataType"),
      },
    };
  }

  /**
   * Expiration notice select: how many days before the expiry the reminder fires.
   *
   * @since 3.0.0
   */
  expirationNotice(className: string): FormlyFieldConfig {
    const values = ["7", "30", "60", "90", "120", "150", "180", "F+30"];
    const options: FieldOption[] = [
      {
        value: "",
        label: this.instant("settings.documentTemplate.noExpirationNotice"),
      },
    ];
    values.forEach((value) => options.push({ value, label: value }));

    return {
      className,
      type: "select",
      key: "expirationNotice",
      templateOptions: {
        label: this.instant(
          "settings.documentTemplate.fields.expirationNotice"
        ),
        required: false,
        multiple: false,
        options,
      },
    };
  }

  /**
   * Expiration period (validity duration) of the document.
   *
   * Left as a free text input on purpose: the back-end accepts an open set of values
   * ('5a', '10a', ...) that is not published, so validating it client side would
   * reject values the server considers legitimate. Quick picks are offered next to
   * the form.
   *
   * @since 3.0.0
   */
  expirationPeriod(className: string): FormlyFieldConfig {
    return {
      className,
      type: "input",
      key: "expirationPeriod",
      templateOptions: {
        label: this.instant(
          "settings.documentTemplate.fields.expirationPeriod"
        ),
        required: false,
        maxLength: 10,
        description: this.instant(
          "settings.documentTemplate.hints.expirationPeriod"
        ),
      },
    };
  }

  /**
   * A boolean rendered as a Material toggle, so that no "Sì"/"No" option label has to
   * be written anywhere.
   *
   * @since 3.0.0
   */
  toggle(key: string, labelKey: string, className: string): FormlyFieldConfig {
    return {
      className,
      type: "toggle",
      key,
      templateOptions: {
        label: this.instant(labelKey),
      },
    };
  }

  /**
   * Generic single choice select.
   *
   * @since 3.0.0
   */
  private select(
    key: string,
    labelKey: string,
    className: string,
    options: FieldOption[]
  ): FormlyFieldConfig {
    return {
      className,
      type: "select",
      key,
      templateOptions: {
        label: this.instant(labelKey),
        required: true,
        multiple: false,
        options,
      },
    };
  }

  /**
   * Translated option.
   *
   * @since 3.0.0
   */
  private option(value: any, labelKey: string): FieldOption {
    return { value, label: this.instant(labelKey) };
  }

  /**
   * Shortcut on the translate service.
   *
   * @since 3.0.0
   */
  private instant(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }
}
